import type { QueryOptions } from "@tanstack/react-query"
import type { ExactPartial } from "@wagmi/core/internal"

import { Evaluate, getAddress } from "viem"
import {
  type GetPerspectiveErrorType,
  type GetPerspectiveParameters,
  type GetPerspectiveReturnType,
  getPerspective,
} from "../actions/getPerspective"
import type { ExtendedConfig, ScopeKeyParameter } from "../types"
import { filterQueryOptions } from "../utils"

export type GetPerspectiveOptions = Evaluate<
  ExactPartial<GetPerspectiveParameters> & ScopeKeyParameter
>

export function getPerspectiveQueryKey(options: GetPerspectiveOptions = {}) {
  const { address, evcAddress, ...parameters } = options
  return [
    "perspective",
    filterQueryOptions({
      address: address ? getAddress(address) : undefined,
      evcAddress: evcAddress ? getAddress(evcAddress) : undefined,
      ...parameters,
    }),
  ] as const
}

export function getPerspectiveQueryFn(
  config: ExtendedConfig,
  options: GetPerspectiveOptions = {},
) {
  const { ...parameters } = options
  return getPerspective(config, parameters)
}

export function getPerspectiveQueryOptions<config extends ExtendedConfig>(
  config: config,
  options: GetPerspectiveOptions,
) {
  return {
    gcTime: 0,
    queryFn: async ({ queryKey }) => {
      return getPerspectiveQueryFn(config, queryKey[1])
    },
    queryKey: getPerspectiveQueryKey(options),
  } satisfies QueryOptions<
    GetPerspectiveQueryFnData,
    GetPerspectiveErrorType,
    GetPerspectiveData,
    GetPerspectiveQueryKey
  >
}

export type GetPerspectiveQueryFnData = GetPerspectiveReturnType

export type GetPerspectiveData = GetPerspectiveQueryFnData

export type GetPerspectiveQueryKey = ReturnType<typeof getPerspectiveQueryKey>
